import * as React from "react";
import {Button, Tab, Tabs} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {connect} from "react-redux";
import {RouteComponentProps} from "react-router";
import {Redirect, withRouter} from "react-router-dom";
import {ActionBar} from "../common/ui/actionbar/ActionBar";
import {DetailHeader} from "../common/ui/detailsection/DetailHeader";
import {DetailView, FieldInfo, Formats} from "../common/ui/detailsection/DetailView";
import {SectionHeader} from "../common/ui/detailsection/SectionHeader";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {MasterDetailLayout} from "../common/ui/layouts/MasterDetailLayout";
import {Previewer} from "../common/ui/riamap/Previewer";
import {UploadOptions} from "../common/ui/upload/UploadOptions";
import {FileUploadAndInjectedProps, WithFileUpload} from "../common/ui/upload/WithFileUpload";
import {getParameterFromOwnProps} from "../common/util/Util";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {WithUpdateClient, WithUpdateClientProps} from "../liveupdate/WithUpdateClient";
import * as paths from "../paths";
import {AddToProductButton} from "../products/AddToProductButton";
import {CreateProductButton} from "../products/CreateProductButton";
import {PublishDataInServiceButton} from "../services/CreateServiceButton";
import {DeleteDataButton} from "./DeleteDataButton";
import {CustomPropertyDetail, ImportedData, readOnlyCustomProperties} from "./model";
import {RefreshDataButton} from "./RefreshDataButton";
import {actions} from "./actions";
import {selectors} from "./selectors";
import {ISOMetadataPopup} from "./ISOMetadataPopup";
import {DataSecurityDialog} from "./DataSecurityDialog";
import {DimensionProps, Dimensions} from "../common/ui/dimensions/Dimensions";
import {fullViewPortDimensions} from "../common/ui/table/ControlRoomTable";
import {OpenFolderButton} from "./OpenFolderButton";
import {Feature} from "@luciad/ria/model/feature/Feature.js";
import {Polygon} from "@luciad/ria/shape/Polygon.js";
import {BatchUpdateButton} from "./BatchUpdateButton";
import {CustomPropertiesDialog} from "./CustomPropertiesDialog";

interface DataDetailPageState {
  data: ImportedData;
  isLoading: boolean;
  success: boolean;
  error: Error;
  deleted: boolean;
  showISOMetadata: boolean;
  showDataSecurityDialog: boolean;
  securitySpatialAreaCoordinates?: number[];
  previewMapDrawModeActive: boolean;
  previewMapEditModeActive: boolean;
  previewMapEditCoordinates: number[];
  customPropertyDetails: CustomPropertyDetail[];
  selectedCustomProperties: CustomPropertyDetail[];
  showCustomPropertiesDialog: boolean;
  topHeight: number;
}

enum TabKey {
  DETAILS = "details",
  CUSTOM_PROPERTIES = "CustomProperties"
}

interface DataDetailPageDispatchProps {
  dataChanged: (data: ImportedData) => void;
}

interface DataDetailPageProps {
  dataId: string;
  loadedData?: ImportedData
  showInPreview?: boolean;
}

const DATA_DETAIL_MESSAGES = defineMessages({
  ID: {
    id: "studio.data.detail.id",
    defaultMessage: "Id",
  },
  PublicID: {
    id: "studio.data.detail.publicid",
    defaultMessage: "PublicId",
  },
  Title: {
    id: "studio.data.detail.title",
    defaultMessage: "Title",
  },
  Abstract: {
    id: "studio.data.detail.abstract",
    defaultMessage: "Abstract",
  },
  Keywords: {
    id: "studio.data.detail.keywords",
    defaultMessage: "Keywords",
  },
  Path: {
    id: "studio.data.detail.path",
    defaultMessage: "Path",
  },
  Format: {
    id: "studio.data.detail.format",
    defaultMessage: "Format",
  },
  FileName: {
    id: "studio.data.detail.file-name",
    defaultMessage: "File Name",
  },
  FileSize: {
    id: "studio.data.detail.file-size",
    defaultMessage: "File Size",
  },
  DateCreated: {
    id: "studio.data.detail.date-created",
    defaultMessage: "Date Created",
  },
  TemporalExtents: {
    id: "studio.data.detail.temporal-extents",
    defaultMessage: "Temporal Extents",
  },
  DetailsLabel: {
    id: "studio.data.detail.tab-details",
    defaultMessage: "Details",
  },
  CustomPropertiesLabel: {
    id: "studio.data.detail.tab-custom-properties",
    defaultMessage: "Properties",
  },
  Separator: {
    id: "studio.data.detail.property-separator",
    defaultMessage: ",",
  },
  InvalidID: {
    id: "studio.data.detail.invalid-id",
    defaultMessage: "Invalid data ID",
  }
});

export class DataDetailPageComponent extends React.Component<RouteComponentProps<{
  id: string
}> & InjectedIntlProps & DimensionProps & DataDetailPageProps & DataDetailPageDispatchProps & WithApiProperties & FileUploadAndInjectedProps & WithUpdateClientProps, DataDetailPageState> {
  DetailPageTopSectionRef: React.RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);
    this.DetailPageTopSectionRef = React.createRef();
    this.state = {
      data: null,
      isLoading: true,
      success: false,
      error: null,
      deleted: false,
      showISOMetadata: false,
      showDataSecurityDialog: false,
      securitySpatialAreaCoordinates: null,
      previewMapDrawModeActive: false,
      previewMapEditModeActive: false,
      previewMapEditCoordinates: null,
      customPropertyDetails: null,
      showCustomPropertiesDialog: false,
      selectedCustomProperties: [],
      topHeight: 250,
    };
  }

  componentDidUpdate() {
    const {topHeight} = this.state;
    const newTopHeight = this.DetailPageTopSectionRef.current?.offsetHeight;
    const bottomPadding = 20;

    if (newTopHeight && newTopHeight + bottomPadding !== topHeight) {
      this.setState({topHeight: newTopHeight + bottomPadding});
    }
  }

  componentDidMount() {
    const {dataId, loadedData} = this.props;
    if (loadedData) {
      this.setState({data: loadedData});
    } else {
      this.getImportedDataById(dataId);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const dataId = nextProps.dataId;
    if (dataId && this.props.dataId !== dataId) {
      this.getImportedDataById(dataId);
    } else if (nextProps.loadedData) {
      this.setState({data: nextProps.loadedData});
    }
  }

  getImportedDataById(id: string) {
    const {intl} = this.props;

    if (id == null) {
      this.setState({
        isLoading: false,
        error: {name: "Invalid ID", message: intl.formatMessage(DATA_DETAIL_MESSAGES.InvalidID)}
      });
    }
    this.setState({isLoading: true});

    Promise.allSettled([
      this.props.api.getImportedDataById(id),
      this.props.api.getCustomProperties()
    ]).then((response) => {
      let data = null;
      let customPropertyDetails = null;
      if (response[0].status === 'fulfilled') {
        const importedDataResponse = response[0] as PromiseFulfilledResult<any>;
        data = importedDataResponse.value;
      }
      if (response[1].status === 'fulfilled') {
        const importedDataResponse = response[1] as PromiseFulfilledResult<any>;
        customPropertyDetails = importedDataResponse.value.customProperties.sort((a, b) => {
          if (a.displayName > b.displayName) {
            return 1
          } else if (b.displayName > a.displayName) {
            return -1
          } else {
            return 0
          }
        });
      }
      this.setState({data, customPropertyDetails, isLoading: false, success: true});
    }).catch(
        (error) => this.setState({data: null, isLoading: false, error}),
    );
  }

  refreshData = (): void => {
    const {dataId} = this.props
    this.getImportedDataById(dataId);
  }

  setDeleted = () => {
    this.setState({deleted: true});
  }

  updateData = (updatedData: ImportedData) => {
    this.setState({data: updatedData});
  }

  openDataSecurityDialog = (): void => {
    this.setState({showDataSecurityDialog: true});
  }

  closeDataSecurityDialog = (): void => {
    this.setState({showDataSecurityDialog: false});
  }

  getDataViewFields = () => {
    const {data} = this.state;
    const {intl, dataChanged} = this.props;
    const {creationTime} = data;
    const dataViewFields = [
      {
        key: "Id",
        name: intl.formatMessage(DATA_DETAIL_MESSAGES.ID),
        value: data.id,
      },
      {
        key: "Title",
        name: intl.formatMessage(DATA_DETAIL_MESSAGES.Title),
        value: data.title,
        format: Formats.TEXT_EDIT,
        onChange: (newTitle) => {
          const updatedData = {...data, title: newTitle};
          dataChanged(updatedData);
        }
      },
      {
        key: "Abstract",
        name: intl.formatMessage(DATA_DETAIL_MESSAGES.Abstract),
        value: data.abstractText,
        format: Formats.TEXT_EDIT,
        onChange: (newAbstract) => {
          const updatedData = {...data, abstractText: newAbstract};
          dataChanged(updatedData);
        }
      },
      {
        key: "Keywords",
        name: intl.formatMessage(DATA_DETAIL_MESSAGES.Keywords),
        value: data.keywords.join(", "),
        format: Formats.TEXT_EDIT,
        onChange: (newKeywords) => {
          const keywordAsArray = newKeywords.split(",").map((str) => str.trim());
          const updatedData = {...data, keywords: keywordAsArray};
          dataChanged(updatedData);
        }
      },
      {
        key: "Path",
        name: intl.formatMessage(DATA_DETAIL_MESSAGES.Path),
        value: data.filePath,
      },
      {
        key: "Format",
        name: intl.formatMessage(DATA_DETAIL_MESSAGES.Format),
        value: data.type,
      },
      {
        key: "File Name",
        name: intl.formatMessage(DATA_DETAIL_MESSAGES.FileName),
        value: data.fileMetadata.name,
      },
      {
        key: "File Size",
        name: intl.formatMessage(DATA_DETAIL_MESSAGES.FileSize),
        value: data.fileMetadata.size,
        format: Formats.FILESIZE,
      },
      {
        key: "Date Created",
        name: intl.formatMessage(DATA_DETAIL_MESSAGES.DateCreated),
        value: creationTime,
        format: Formats.DATETIME,
      },
      {
        key: "Temporal Extents",
        name: intl.formatMessage(DATA_DETAIL_MESSAGES.TemporalExtents),
        value: data.temporalExtents,
        format: Formats.TEMPORAL_EXTENTS_EDIT,
        onChange: (newTemporalExtents) => {
          const updatedData = {...data, temporalExtents: newTemporalExtents};
          dataChanged(updatedData);
        }
      },
    ];

    const addCPToDetailsTab = ["bands", "type", "dataType", "crs_horizontal", "crs_vertical", "pyramids", "statistics"];
    const joinedProperties = [
      {
        displayName: "Pixel Size (X,Y)",
        propertyNames: ["pixelSizeX", "pixelSizeY"],
        finalProperty: "unit"
      },
      {
        displayName: "Image Dimensions (width,height)",
        propertyNames: ["width", "height"],
        finalString: "Pixels"
      }
    ];
    const skippedProperties = ["thumbnail", "parent"];
    return dataViewFields.concat(this.getCustomPropertyFields(addCPToDetailsTab, joinedProperties, skippedProperties));
  }

  getCustomPropertyFields = (list?, joinedProperties?, skippedProperties?) => {
    const {data, customPropertyDetails, selectedCustomProperties} = this.state;
    const customPropertyFields = [];
    customPropertyDetails.forEach((customProperty) => {

      if (joinedProperties) {
        joinedProperties.forEach((joinedProperty) => {
          if (customProperty.displayName > joinedProperty.displayName) {
            this.addJoinedProperty(joinedProperty, joinedProperties, customPropertyFields);
          }
        });
      }
      if ((!skippedProperties.includes(customProperty.name)) &&
          (!list || (list?.includes(customProperty.name)))) {
        this.addDataViewField(customPropertyFields, customProperty.name, customProperty.displayName,
            data.customProperties[customProperty.name], customProperty);
      }
    });
    if (joinedProperties) {
      joinedProperties.forEach((joinedProperty) => {
        this.addJoinedProperty(joinedProperty, joinedProperties, customPropertyFields);
      });
    }

    selectedCustomProperties.forEach((customProperty) => {
      let newValue: any = null;
      if (customProperty?.type === "String") {
        newValue = "Enter a value";
      }
      this.addDataViewField(customPropertyFields, customProperty.name, `* ${customProperty.displayName}`,
          newValue, customProperty);
    });

    return customPropertyFields;
  }

  addJoinedProperty = (joinedProperty, joinedProperties, customPropertyFields) => {
    const {data} = this.state;
    const {intl} = this.props;
    const key = joinedProperty.propertyNames.join('_');
    const displayName = joinedProperty.displayName;
    let value = joinedProperty.propertyNames.length >= 2 && data.customProperties[joinedProperty.propertyNames[0]] &&
                data.customProperties[joinedProperty.propertyNames[1]] ?
                `${data.customProperties[joinedProperty.propertyNames[0]]}${intl.formatMessage(
                    DATA_DETAIL_MESSAGES.Separator)} ${data.customProperties[joinedProperty.propertyNames[1]]}` : null;
    if (value) {
      value = joinedProperty.finalProperty && data.customProperties[joinedProperty.finalProperty]
              ? `${value} ${data.customProperties[joinedProperty.finalProperty]}` : value;
      value = joinedProperty.finalString ? `${value} ${joinedProperty.finalString}` : value;
    }

    this.addDataViewField(customPropertyFields, key, displayName, value, null);
    joinedProperties.pop(joinedProperty);
  }

  addDataViewField = (dataViewFields, key, name, value, customProperty: CustomPropertyDetail) => {
    let newField: FieldInfo = {key, name, value: value || ""};

    if (!readOnlyCustomProperties.includes(key)) {
      switch (customProperty?.type) {
      case "Enum":
      case "String":
        newField = {
          ...newField,
          value: value ? String(value) : null,
          format: (customProperty.possibleValues) ? Formats.SELECT : Formats.TEXT_EDIT,
          options: (customProperty.possibleValues) ? customProperty.possibleValues : null,
          onChange: (newValue) => this.onDataQueryableValueChange(key, newValue),
        }
        break;
      case "Long":
        newField = {
          ...newField,
          value: !isNaN(parseInt(value, 10)) ? parseInt(value, 10) : "",
          format: (customProperty.possibleValues) ? Formats.SELECT : Formats.LONG_EDIT,
          options: (customProperty.possibleValues) ? customProperty.possibleValues : null,
          onChange: (newValue) => this.onDataQueryableValueChange(key, newValue),
        }
        break;
      case "Double":
        newField = {
          ...newField,
          value: !isNaN(parseFloat(value)) ? parseFloat(value) : "",
          format: (customProperty.possibleValues) ? Formats.SELECT : Formats.DOUBLE_EDIT,
          options: (customProperty.possibleValues) ? customProperty.possibleValues : null,
          onChange: (newValue) => this.onDataQueryableValueChange(key, newValue),
        }
        break;
      case "Boolean":
        newField = {
          ...newField,
          value: value === true || value === false ? value : null,
          format: Formats.BOOL_EDIT,
          onChange: (newValue) => this.onDataQueryableValueChange(key, newValue),
        }
        break;
      case "Date":
        newField = {
          ...newField,
          value: value || null,
          format: Formats.DATETIME_EDIT,
          onChange: (newValue) => this.onDataQueryableValueChange(key, newValue)
        }
        break;
      }
    }

    if (typeof value !== "undefined") {
      dataViewFields.push(newField)
    }
  }

  drawSecuritySpatialArea = (): void => {
    this.setState({
      previewMapDrawModeActive: true,
      previewMapEditModeActive: false,
    });
  }

  editSecuritySpatialArea = (coordinates: number[]): void => {
    this.setState({
      previewMapEditModeActive: true,
      previewMapDrawModeActive: false,
      previewMapEditCoordinates: coordinates,
    });
  }

  private onDrawCancel = () => {
    this.setState({
      previewMapDrawModeActive: false,
    });
  }

  private onEditCancel = () => {
    this.setState({
      previewMapEditModeActive: false,
    });
  }

  private onDataQueryableValueChange = (propertyName, value) => {
    const {selectedCustomProperties, data, customPropertyDetails} = this.state;
    const updatedData = data;
    const index = selectedCustomProperties.map(cp => cp.name).indexOf(propertyName);
    if (index >= 0) {
      selectedCustomProperties.splice(index, 1);  //delete saved value from unsaved list
    }

    if (updatedData.customProperties[propertyName] === value) {
      return;
    }
    if (value !== null) {
      updatedData.customProperties[propertyName] = value;
    } else {
      const deletedDetail = customPropertyDetails.find((detail) => detail.name === propertyName);
      if (deletedDetail) {
        selectedCustomProperties.push(deletedDetail);
      }

      delete updatedData.customProperties[propertyName];
    }
    return this.props.api.updateProperties(updatedData)
        .then(() =>
            this.props.api.getImportedDataById(this.props.dataId)
        )
        .then((importedData) =>
            this.setState({data: importedData})
        ).catch((error) =>
            this.setState({error})
        );

  }

  onPreviewFeatureDrawn = (feature: Feature): boolean => {
    const polygon = feature.shape as Polygon;
    const numPoints = polygon.pointCount;
    const coords: number[] = [];
    for (let i = 0; i < numPoints; i++) {
      const point = polygon.getPoint(i);
      coords.push(point.x);
      coords.push(point.y);
    }

    this.setState({
      securitySpatialAreaCoordinates: coords,
      previewMapDrawModeActive: false,
      previewMapEditModeActive: false,
      previewMapEditCoordinates: null,
    });
    return true;
  }

  openCustomPropertiesDialog = () => {
    this.setState({
      showCustomPropertiesDialog: true,
    });
  };

  closeCustomPropertiesDialog = () => {
    this.setState({
      showCustomPropertiesDialog: false,
    });
  };

  updateSelectedCustomProperties = (selectedCustomProperties: CustomPropertyDetail[]) => {
    this.setState({selectedCustomProperties});
  }

  getSelectedCustomProperties = (): CustomPropertyDetail[] => {
    return this.state.selectedCustomProperties;
  }

  getCurrentCustomProperties = (): CustomPropertyDetail[] => {
    const {data, customPropertyDetails} = this.state;
    const currentCustomProperties: CustomPropertyDetail[] = [];

    customPropertyDetails.forEach((property) => {
      if (data?.customProperties[property.name]) {
        currentCustomProperties.push(property);
      }
    })

    return currentCustomProperties;
  }

  render() {
    const {
      isLoading,
      success,
      error,
      data,
      deleted,
      showDataSecurityDialog,
      securitySpatialAreaCoordinates,
      showCustomPropertiesDialog,
      topHeight
    } = this.state;
    const {api} = this.props;
    if (deleted) {
      return <Redirect to="/data"/>;
    } else if (!isLoading && error) {
      return <ErrorDisplay error={error}/>;
    } else if (!isLoading && success) {
      const {id} = data;
      const {containerHeight} = this.props;
      const maxHeight = containerHeight - topHeight;
      const skippedProperties = ["thumbnail", "parent", "bands", "type", "dataType", "crs_horizontal", "crs_vertical",
                                 "pyramids", "statistics", "width", "height", "pixelSizeX", "pixelSizeY", "unit"];
      const masterPane = (
          <div>
            {this.state.showISOMetadata &&
             <ISOMetadataPopup dataId={id} onHide={() => this.setState({showISOMetadata: false})}/>}
            <div ref={this.DetailPageTopSectionRef}>
              <DetailHeader
                  title={data.title}
                  buttons={[
                    <ActionBar key="actionbar">
                      <OpenFolderButton key="open-folder-button" data={data}/>
                      <RefreshDataButton key="refresh-data-button"
                                         data={data}
                                         onRefreshSuccess={this.updateData}
                                         onRefreshError={Function.prototype}/>
                      <DeleteDataButton key="delete-data-button"
                                        disabled={false}
                                        data={[data]}
                                        onDeleteSuccess={this.setDeleted}
                                        onDeleteError={Function.prototype}
                                        detail={true}
                                        onDeleteWarnings={Function.prototype}
                                        onCloseWarnings={this.setDeleted}/>
                      <Button key="data-security-button"
                              data-cy="data-security-button"
                              onClick={this.openDataSecurityDialog}>
                        <LcdIcon icon="lock-fill"/>
                        <FormattedMessage id="studio.data.data-detail-page.security"
                                          defaultMessage="Security"/>
                      </Button>
                      <BatchUpdateButton
                          key="data-batch-update-button"
                          dataId={this.props.dataId}
                          refreshData={this.refreshData}
                      />
                    </ActionBar>
                  ]}
              />
              <ActionBar>
                <PublishDataInServiceButton disabled={false} inputData={[data]}/>
                <CreateProductButton initialData={[data]}/>
                <AddToProductButton initialData={[data]}/>
              </ActionBar>

              <ActionBar>
                <SectionHeader>
                  <FormattedMessage id="studio.data.data-detail-page.metadata-header"
                                    defaultMessage="ISO Metadata"/>
                </SectionHeader>
                <div>
                  <Button onClick={() => this.setState({showISOMetadata: true})}>
                    <LcdIcon icon="list-alt"/> <FormattedMessage
                      id="studio.data.data-detail-page.show-iso-metadata"
                      defaultMessage="Show"/>
                  </Button>
                </div>
                <div>
                  <Button onClick={() => {
                    api.downloadApolloMetadata(id);
                  }}>
                    <LcdIcon icon="download-alt"/><FormattedMessage
                      id="studio.data.data-detail-page.download"
                      defaultMessage="Download"/>
                  </Button>
                </div>
              </ActionBar>
            </div>
            <Tabs id="DataDetailTabs" bsStyle="pills" justified={false} animation={true} className={"tabs"}
                  defaultActiveKey={TabKey.DETAILS}>
              <Tab eventKey={TabKey.DETAILS}
                   title={this.props.intl.formatMessage(DATA_DETAIL_MESSAGES.DetailsLabel)}>
                <div style={{
                  display: 'block',
                  overflow: 'hidden',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight
                }}>
                  <DetailView fields={this.getDataViewFields()}/>
                </div>
              </Tab>
              <Tab eventKey={TabKey.CUSTOM_PROPERTIES}
                   title={this.props.intl.formatMessage(DATA_DETAIL_MESSAGES.CustomPropertiesLabel)}>
                <div style={{
                  display: 'block',
                  overflow: 'hidden',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight
                }}>
                  <DetailView
                      fields={this.getCustomPropertyFields(null, null, skippedProperties)}/>
                </div>
                <Button onClick={this.openCustomPropertiesDialog}>
                  <LcdIcon icon={"plus"}/>
                  <FormattedMessage id="studio.files-list.add-button" defaultMessage="Add"/>
                </Button>
              </Tab>
            </Tabs>
          </div>
      );

      const getPreviewMetadataFunction = api.getImportedDataPreviewMetadata;
      const detailPane = (
          <Previewer wmsBaseUrl={paths.PREVIEW_DATA_PATH}
                     background={api.getPreviewBackground()}
                     dataToVisualize={this.state.data ? [this.state.data] : []}
                     shouldFit={true}
                     getPreviewMetadataFunction={getPreviewMetadataFunction}
                     drawModeActive={this.state.previewMapDrawModeActive}
                     editModeActive={this.state.previewMapEditModeActive}
                     onDrawCancel={this.onDrawCancel}
                     onEditCancel={this.onEditCancel}
                     editFeatureCoordinates={this.state.previewMapEditCoordinates}
                     onFeatureDrawn={this.onPreviewFeatureDrawn}
          />
      );
      return <div>
        <MasterDetailLayout masterPane={masterPane} detailPane={detailPane}/>
        {showDataSecurityDialog && <DataSecurityDialog key={`data-security-dialog`}
                                                       previewMapDrawModeActive={this.state.previewMapDrawModeActive}
                                                       previewMapEditModeActive={this.state.previewMapEditModeActive}
                                                       onHide={this.closeDataSecurityDialog}
                                                       dataId={this.props.dataId}
                                                       dataType={data.type}
                                                       onDrawSpatialArea={this.drawSecuritySpatialArea}
                                                       onEditSpatialArea={this.editSecuritySpatialArea}
                                                       spatialAreaCoordinates={securitySpatialAreaCoordinates}
        />}
        {showCustomPropertiesDialog &&
         <CustomPropertiesDialog api={this.props.api} onClose={this.closeCustomPropertiesDialog}
                                 onUpdate={this.updateSelectedCustomProperties}
                                 getCurrentCustomProperties={this.getCurrentCustomProperties}
                                 getSelectedCustomProperties={this.getSelectedCustomProperties}
         />}
      </div>;
    } else if (isLoading) {
      return <p><FormattedMessage id="studio.data.data-detail-page.loading" defaultMessage="Loading..."/></p>;
    }
    return <p><FormattedMessage id="studio.data.data-detail-page.unknown-state"
                                defaultMessage="Unknown data detail page state!"/></p>;
  }
}

const mapStateToProps = (state, ownProps) => {
  // either passed as own properties or as a router parameter
  const id = getParameterFromOwnProps(ownProps, "dataId"); //maybe injected by withRouter
  const loadedData = selectors.getDataById(state, id);
  return {
    dataId: id,
    // The data in the store could be missing the detailed data info that you only get when doing GET /api/data/{id}
    // If this is not present then the detailed data will be fetched in componentDidMount
    loadedData: loadedData?.fileMetadata ? loadedData : null,
    createFormData: (uploadFiles: File[]) => {
      const data = new FormData();
      data.append("importedDataId", id);
      data.append("metadataFile", uploadFiles[0]);
      return data;
    },
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    dataChanged: (data: ImportedData) => {
      dispatch(actions.updateData(data));
    },
  };
};

const metadataUploadOptions: UploadOptions = {
  allowedFileExtension: null,
  allowMultipleFiles: false,
  uploadUri: "/upload/metadata",
};

const DataDetailPageComponentWithFileUpload = WithFileUpload(metadataUploadOptions)(
    injectIntl(DataDetailPageComponent));

export const DataDetailPage = connect(mapStateToProps, mapDispatchToProps)(
    withRouter(WithApi(WithUpdateClient(Dimensions(fullViewPortDimensions)(DataDetailPageComponentWithFileUpload)))));